import React, { useMemo } from 'react';

import { paths } from 'src/routes/paths';

import { RoleHelpers } from 'src/utils/constants';

import { useGetModules } from 'src/api/services/module-permission/modulePermissionService';

import { Iconify } from 'src/components/iconify';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------
// `config-nav-dashboard.jsx` dosyası, uygulamanın navigasyon menüsünü dinamik olarak oluşturan, API'den gelen modül bilgilerine göre menü öğelerini sıralayan, kullanıcının erişim izinlerine göre filtreleme yapan ve performans optimizasyonları ile hata yönetimi sağlayan temel bir bileşendir.

const icon = (iconName) => <Iconify icon={iconName} sx={{ mr: 1 }} />;

const ICONS = {
  home: icon('solar:home-angle-bold-duotone'),
  schedule: icon('solar:calendar-minimalistic-bold-duotone'),
  documents: icon('solar:document-add-bold-duotone'),
  school: icon('solar:home-bold-duotone'),
  user: icon('solar:user-id-bold-duotone'),
  fair: icon('solar:user-hand-up-bold-duotone'),
  competition: icon('solar:atom-bold-duotone'),
  project: icon('solar:bookmark-square-minimalistic-bold-duotone'),
  participation: icon('solar:users-group-rounded-bold-duotone'),
  payment: icon('solar:dollar-bold-duotone'),
  evaluation: icon('solar:checklist-minimalistic-bold-duotone'),
  travel: icon('ph:airplane-takeoff-duotone'),
  performance: icon('solar:masks-bold-duotone'),
  trip: icon('solar:bus-bold-duotone'),
  accommodation: icon('solar:home-angle-bold-duotone'),
  printable: icon('solar:printer-2-bold-duotone'),
  certificate: icon('ph:certificate-duotone'),
  system: icon('solar:settings-minimalistic-bold-duotone'),
  define: icon('solar:verified-check-bold-duotone'),
  superDefine: icon('solar:shield-check-bold-duotone'),
};

// Modül türlerine göre varsayılan sıralama değerleri
const DEFAULT_SORT_VALUES = {
  // Ana modüller
  home: 10,
  schedule: 15,
  documents: 20,
  fair: 30,
  school: 40,
  project: 50,
  participation: 60,
  user: 70,
  travel: 75,
  performance: 76,
  payment: 80,
  judgement: 85,
  judging: 90,
  define: 100,
  super_define: 110,

  // Alt modüller için varsayılan değerler
  system: 10,
  geography: 20,
  competition: 30,
  permission: 40,
  authorization: 50,
  discipline: 60,
  company: 70,
  organization: 80,

  // Extras
  extras: 500,

  // Diğer
  default: 999,
};

// ----------------------------------------------------------------------

export const ConfigNavDashboard = () => {
  const { user } = useAuthContext();
  const { modules, modulesLoading, modulesError } = useGetModules();

  const visibleModules = user?.VisibleModuleIds;

  // Modüllerin yüklenme durumunu ve hataları kontrol et
  React.useEffect(() => {
    if (!modulesLoading) {
      if (modulesError) {
        console.error('Error loading modules:', modulesError);
      }
    }
  }, [modules, modulesLoading, modulesError]);

  // Modül eşleştirme fonksiyonlarını memoize et
  const { getModuleId, getModuleSortId } = useMemo(() => {
    // Modül ID'lerini API'den gelen verilerle eşleştiren yardımcı fonksiyon
    const findModuleId = (name, defaultId) => {
      if (modulesLoading || !modules.length) return defaultId;

      // Önce tam eşleşme ara
      let foundModule = modules.find(
        (module) =>
          module.Name.toLowerCase() === name.toLowerCase() ||
          module.Description.toLowerCase() === name.toLowerCase()
      );

      // Tam eşleşme bulunamazsa, içeren bir eşleşme ara
      if (!foundModule) {
        foundModule = modules.find(
          (module) =>
            module.Name.toLowerCase().includes(name.toLowerCase()) ||
            module.Description.toLowerCase().includes(name.toLowerCase()) ||
            name.toLowerCase().includes(module.Name.toLowerCase())
        );
      }

      // Path'e göre eşleştirme (özellikle alt modüller için)
      if (!foundModule && name.includes(' ')) {
        // Birleşik isimler için tire ile ayrılmış path kontrolü
        const pathName = name.toLowerCase().replace(/ /g, '-');
        foundModule = modules.find(
          (module) => module.Path && module.Path.toLowerCase().includes(pathName)
        );
      }

      return foundModule ? foundModule.Id : defaultId;
    };

    // Modül SortId'sini API'den gelen verilerle eşleştiren yardımcı fonksiyon
    const findModuleSortId = (name, defaultSortId) => {
      // Modül türüne göre varsayılan değer belirle
      let defaultValue = DEFAULT_SORT_VALUES.default;

      const lowerName = name.toLowerCase();
      if (lowerName === 'home') defaultValue = DEFAULT_SORT_VALUES.home;
      else if (lowerName === 'schedule') defaultValue = DEFAULT_SORT_VALUES.schedule;
      else if (lowerName === 'documents') defaultValue = DEFAULT_SORT_VALUES.documents;
      else if (lowerName.includes('fair')) defaultValue = DEFAULT_SORT_VALUES.fair;
      else if (lowerName.includes('school') || lowerName.includes('organization'))
        defaultValue = DEFAULT_SORT_VALUES.school;
      else if (lowerName.includes('project')) defaultValue = DEFAULT_SORT_VALUES.project;
      else if (lowerName.includes('participation'))
        defaultValue = DEFAULT_SORT_VALUES.participation;
      else if (lowerName === 'user') defaultValue = DEFAULT_SORT_VALUES.user;
      else if (lowerName === 'travel') defaultValue = DEFAULT_SORT_VALUES.travel;
      else if (lowerName.includes('payment')) defaultValue = DEFAULT_SORT_VALUES.payment;
      else if (lowerName === 'judgement') defaultValue = DEFAULT_SORT_VALUES.judgement;
      else if (lowerName.includes('judging')) defaultValue = DEFAULT_SORT_VALUES.judging;
      else if (lowerName === 'define') defaultValue = DEFAULT_SORT_VALUES.define;
      else if (lowerName.includes('super define')) defaultValue = DEFAULT_SORT_VALUES.super_define;
      else if (lowerName.includes('system')) defaultValue = DEFAULT_SORT_VALUES.system;
      else if (lowerName.includes('geography')) defaultValue = DEFAULT_SORT_VALUES.geography;
      else if (lowerName.includes('competition')) defaultValue = DEFAULT_SORT_VALUES.competition;
      else if (lowerName.includes('judgement')) defaultValue = DEFAULT_SORT_VALUES.judgement;
      else if (lowerName.includes('permission')) defaultValue = DEFAULT_SORT_VALUES.permission;
      else if (lowerName.includes('authorization'))
        defaultValue = DEFAULT_SORT_VALUES.authorization;
      else if (lowerName.includes('discipline')) defaultValue = DEFAULT_SORT_VALUES.discipline;
      else if (lowerName.includes('company')) defaultValue = DEFAULT_SORT_VALUES.company;

      // Eğer özel bir defaultSortId verilmişse, onu kullan
      const finalDefaultValue = defaultSortId || defaultValue;

      if (modulesLoading || !modules.length) return finalDefaultValue;

      // Önce tam eşleşme ara
      let foundModule = modules.find(
        (module) =>
          module.Name.toLowerCase() === lowerName || module.Description.toLowerCase() === lowerName
      );

      // Tam eşleşme bulunamazsa, içeren bir eşleşme ara
      if (!foundModule) {
        foundModule = modules.find(
          (module) =>
            module.Name.toLowerCase().includes(lowerName) ||
            module.Description.toLowerCase().includes(lowerName) ||
            lowerName.includes(module.Name.toLowerCase())
        );
      }

      return foundModule ? foundModule.SortId : finalDefaultValue;
    };

    return {
      getModuleId: findModuleId,
      getModuleSortId: findModuleSortId,
    };
  }, [modules, modulesLoading]);

  // Recursive sıralama fonksiyonunu memoize et
  const sortItemsByOrder = useMemo(
    () => (items) => {
      // Sort current level items
      const sortedItems = [...items].sort((a, b) => a.sortId - b.sortId);

      // Sort children recursively
      return sortedItems.map((item) => {
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: sortItemsByOrder(item.children),
          };
        }
        return item;
      });
    },
    []
  );

  // NavData'yı memoize et
  const navData = useMemo(
    () => [
      {
        subheader: 'Overview',
        items: [
          {
            title: 'Home',
            path: paths.dashboard.root,
            icon: ICONS.home,
            moduleId: getModuleId('Home', 1),
            sortId: getModuleSortId('Home'),
          },
          {
            title: 'Schedule',
            path: paths.dashboard.schedule.root,
            icon: ICONS.schedule,
            moduleId: getModuleId('Schedule', 42),
            sortId: getModuleSortId('Schedule'),
          },
          {
            title: 'Documents',
            path: paths.dashboard.documents.root,
            icon: ICONS.documents,
            moduleId: getModuleId('Documents', 2),
            sortId: getModuleSortId('Documents'),
          },
          {
            title: 'Affiliated Fair',
            path: paths.dashboard.fair.root,
            icon: ICONS.fair,
            moduleId: getModuleId('Fair', 14),
            sortId: getModuleSortId('Fair'),
          },
          {
            title: 'School',
            path: paths.dashboard.organization.root,
            icon: ICONS.school,
            moduleId: getModuleId('School', 3),
            sortId: getModuleSortId('School'),
          },
          {
            title: 'Competition',
            path: paths.dashboard.competition.root,
            icon: ICONS.competition,
            moduleId: 4,
          },
          {
            title: 'Project',
            path: paths.dashboard.projects.root,
            icon: ICONS.project,
            moduleId: getModuleId('Project', 5),
            sortId: getModuleSortId('Project'),
          },
          {
            title: 'Participation',
            path: paths.dashboard.participation.root,
            icon: ICONS.participation,
            moduleId: getModuleId('Participation', 6),
            sortId: getModuleSortId('Participation'),
            children: [
              {
                title: 'Participant',
                path: paths.dashboard.participation.participant.root,
                moduleId: getModuleId('Participant', 7),
                sortId: getModuleSortId('Participant'),
              },
              {
                title: 'VIP',
                path: paths.dashboard.participation.vip.root,
                moduleId: getModuleId('VIP', 9),
                sortId: getModuleSortId('VIP'),
              },
              {
                title: 'Finalist',
                path: paths.dashboard.participation.finalist.root,
                moduleId: getModuleId('Finalist', 15),
                sortId: getModuleSortId('Finalist'),
              },
              {
                title: 'Invitation Letter',
                path: paths.dashboard.participation.invitation_letter,
                moduleId: getModuleId('Invitation Letter', 8),
                sortId: getModuleSortId('Invitation Letter'),
              },
            ],
          },
          {
            title: 'User',
            path: paths.dashboard.user.root,
            icon: ICONS.user,
            moduleId: getModuleId('User', 10),
            sortId: getModuleSortId('User'),
          },
          {
            title: 'Travel',
            path: paths.dashboard.travel.root,
            icon: ICONS.travel,
            moduleId: getModuleId('Travel', 19),
            sortId: getModuleSortId('Travel'),
          },
          {
            title: 'System',
            path: paths.dashboard.system.root,
            icon: ICONS.system,
            moduleId: getModuleId('System', 40),
            sortId: getModuleSortId('System'),
            children: [
              {
                title: 'Logger',
                path: paths.dashboard.system.logger.root,
                moduleId: getModuleId('Logger', 24),
                sortId: getModuleSortId('Logger'),
              },
              {
                title: 'Feedback',
                path: paths.dashboard.system.feedback.root,
                moduleId: getModuleId('Feedback', 41),
                sortId: getModuleSortId('Feedback'),
              },
              {
                title: 'Survey',
                path: paths.dashboard.system.survey.root,
                moduleId: getModuleId('Survey', 44),
                sortId: getModuleSortId('Survey'),
              },
            ],
          },
          {
            title: 'Accommodation',
            path: paths.dashboard.accommodation.root,
            icon: ICONS.accommodation,
            moduleId: getModuleId('Accommodation', 30),
            sortId: getModuleSortId('Accommodation'),
            children: [
              {
                title: 'Hotel',
                path: paths.dashboard.accommodation.hotel.root,
                moduleId: getModuleId('Hotel', 31),
                sortId: getModuleSortId('Hotel'),
              },
              {
                title: 'Room',
                path: paths.dashboard.accommodation.room.root,
                moduleId: getModuleId('Room', 32),
                sortId: getModuleSortId('Room'),
              },
              {
                title: 'Room Allocation',
                path: paths.dashboard.accommodation.roomAllocation.root,
                moduleId: getModuleId('Room Allocation', 46),
                sortId: getModuleSortId('Room Allocation'),
              },
            ],
          },
          {
            title: 'Trip',
            path: paths.dashboard.trip.root,
            icon: ICONS.trip,
            moduleId: getModuleId('Trip', 30),
            sortId: getModuleSortId('Trip', DEFAULT_SORT_VALUES.extras + 10),
            children: [
              {
                title: 'Tour',
                path: paths.dashboard.trip.tour.root,
                icon: ICONS.tour,
                moduleId: getModuleId('Tour', 30),
                sortId: getModuleSortId('Tour'),
              },
              {
                title: 'Tour Reservation',
                path: paths.dashboard.trip.tourReservation.root,
                icon: ICONS.booking,
                moduleId: getModuleId('Tour Reservation', 31),
                sortId: getModuleSortId('Tour Reservation'),
              },
            ],
          },
          {
            title: 'Performance',
            path: paths.dashboard.performance.root,
            icon: ICONS.performance,
            moduleId: getModuleId('Performance', 20),
            sortId: getModuleSortId('Performance'),
          },
          {
            title: 'Payment',
            path: paths.dashboard.payment.root,
            icon: ICONS.payment,
            moduleId: getModuleId('Payment', 11),
            sortId: getModuleSortId('Payment'),
          },
          {
            title: 'Printable',
            path: paths.dashboard.printable.root,
            icon: ICONS.printable,
            moduleId: getModuleId('Printable', 34),
            sortId: getModuleSortId('Printable'),
            children: [
              {
                title: 'Participant Badge',
                path: paths.dashboard.printable.participant_badge.root,
                moduleId: getModuleId('Participant Badge', 35),
                sortId: getModuleSortId('Participant Badge'),
              },
              {
                title: 'Consent Form',
                path: paths.dashboard.printable.consent_form.root,
                moduleId: getModuleId('Consent Form', 36),
                sortId: getModuleSortId('Consent Form'),
              },
              {
                title: 'Project Board Banner',
                path: paths.dashboard.printable.project_board_banner.root,
                moduleId: getModuleId('Project Board Banner', 45),
                sortId: getModuleSortId('Project Board Banner'),
              },
            ],
          },
          {
            title: 'Certificate',
            path: paths.dashboard.certificate.root,
            icon: ICONS.certificate,
            moduleId: getModuleId('Certificate', 38),
            sortId: getModuleSortId('Certificate'),
            children: [
              {
                title: 'Participant Certificate',
                path: paths.dashboard.certificate.participant_certificate.root,
                moduleId: getModuleId('Participant Certificate', 37),
                sortId: getModuleSortId('Participant Certificate'),
              },
              {
                title: 'Competition Certificate',
                path: paths.dashboard.certificate.competition_certificate.root,
                moduleId: getModuleId('Competition Certificate', 43),
                sortId: getModuleSortId('Competition Certificate'),
              },
              {
                title: 'Project Award',
                path: paths.dashboard.certificate.project_award.root,
                moduleId: getModuleId('Project Award', 39),
                sortId: getModuleSortId('Project Award'),
              },
            ],
          },
          {
            title: 'Evaluation',
            path: paths.dashboard.judgement.root,
            icon: ICONS.evaluation,
            moduleId: getModuleId('Evaluation', 23),
            sortId: getModuleSortId('Evaluation'),
            children: [
              {
                title: 'Judging Session',
                path: paths.dashboard.judgement.judging_session,
                moduleId: getModuleId('Judging Session', 12),
                sortId: getModuleSortId('Judging Session'),
              },
              {
                title: 'Project Evaluation',
                path: paths.dashboard.judgement.judgement_evaluation.root,
                moduleId: getModuleId('Project Evaluation', 16),
                sortId: getModuleSortId('Project Evaluation'),
              },
              {
                title: 'Evaluation Overview',
                path: paths.dashboard.judgement.evaluation_overview,
                moduleId: getModuleId('Evaluation Overview', 24),
                sortId: getModuleSortId('Evaluation Overview'),
              },
              {
                title: 'Ranking',
                path: paths.dashboard.judgement.ranking,
                moduleId: getModuleId('Ranking', 29),
                sortId: getModuleSortId('Ranking'),
              },
              {
                title: 'Judge',
                path: paths.dashboard.judgement.jury.root,
                moduleId: getModuleId('Judge', 22),
                sortId: getModuleSortId('Judge'),
              },
            ],
          },
          {
            title: 'Define',
            path: paths.dashboard.define.root,
            icon: ICONS.define,
            moduleId: getModuleId('Define', 17),
            sortId: getModuleSortId('Define'),
            children: [
              {
                title: 'Define System',
                path: paths.dashboard.define.system.root,
                moduleId: getModuleId('Define System', 1100),
                sortId: getModuleSortId('Define System'),
                children: [
                  {
                    title: 'Age Interval',
                    path: paths.dashboard.define.system.age_interval,
                    moduleId: getModuleId('Age Interval', 1101),
                    sortId: getModuleSortId('Age Interval'),
                  },
                  {
                    title: 'Disability Type',
                    path: paths.dashboard.define.system.disability_type,
                    moduleId: getModuleId('Disability Type', 1102),
                    sortId: getModuleSortId('Disability Type'),
                  },
                  {
                    title: 'Feedback Type',
                    path: paths.dashboard.define.system.feedback_type,
                    moduleId: getModuleId('Feedback Type', 1103),
                    sortId: getModuleSortId('Feedback Type'),
                  },
                  {
                    title: 'Gender',
                    path: paths.dashboard.define.system.gender,
                    moduleId: getModuleId('Gender', 1104),
                    sortId: getModuleSortId('Gender'),
                  },
                  {
                    title: 'Grade',
                    path: paths.dashboard.define.system.grade,
                    moduleId: getModuleId('Grade', 1105),
                    sortId: getModuleSortId('Grade'),
                  },
                  {
                    title: 'Meal Preference',
                    path: paths.dashboard.define.system.meal_preference,
                    moduleId: getModuleId('Meal Preference', 1106),
                    sortId: getModuleSortId('Meal Preference'),
                  },
                  {
                    title: 'Project File Type',
                    path: paths.dashboard.define.system.project_file_type,
                    moduleId: getModuleId('Project File Type', 1107),
                    sortId: getModuleSortId('Project File Type'),
                  },
                  {
                    title: 'Transportation Type',
                    path: paths.dashboard.define.system.transportation_type,
                    moduleId: getModuleId('Transportation Type', 1108),
                    sortId: getModuleSortId('Transportation Type'),
                  },
                  {
                    title: 'Visa Type',
                    path: paths.dashboard.define.system.visa_type,
                    moduleId: getModuleId('Visa Type', 1109),
                    sortId: getModuleSortId('Visa Type'),
                  },
                  {
                    title: 'Shirt Size',
                    path: paths.dashboard.define.system.shirt_size,
                    moduleId: getModuleId('Shirt Size', 1110),
                    sortId: getModuleSortId('Shirt Size'),
                  },
                ],
              },
              {
                title: 'Competition',
                path: paths.dashboard.define.competition.root,
                moduleId: getModuleId('Competition', 1200),
                sortId: getModuleSortId('Competition', 1200),
                children: [
                  {
                    title: 'Application Type',
                    path: paths.dashboard.define.competition.application_type,
                    moduleId: getModuleId('Application Type', 1201),
                    sortId: getModuleSortId('Application Type', 1201),
                  },
                  {
                    title: 'Competition Payment Type',
                    path: paths.dashboard.define.competition.competition_payment_type,
                    moduleId: getModuleId('Competition Payment Type', 1202),
                    sortId: getModuleSortId('Competition Payment Type', 1202),
                  },
                  {
                    title: 'Performance Type',
                    path: paths.dashboard.define.competition.performance_type,
                    moduleId: getModuleId('Performance Type', 1204),
                    sortId: getModuleSortId('Performance Type', 1204),
                  },
                  {
                    title: 'Project Participation Type',
                    path: paths.dashboard.define.competition.project_participation_type,
                    moduleId: getModuleId('Project Participation Type', 1205),
                    sortId: getModuleSortId('Project Participation Type', 1205),
                  },
                  {
                    title: 'Project Award Type',
                    path: paths.dashboard.define.competition.project_award_type,
                    moduleId: getModuleId('Project Award Type', 1206),
                    sortId: getModuleSortId('Project Award Type', 1206),
                  },
                  {
                    title: 'Consent Term',
                    path: paths.dashboard.define.competition.consent_term,
                    moduleId: getModuleId('Consent Term', 1207),
                    sortId: getModuleSortId('Consent Term', 1207),
                  },
                  {
                    title: 'Venue',
                    path: paths.dashboard.define.competition.venue,
                    moduleId: getModuleId('Venue', 1208),
                    sortId: getModuleSortId('Venue', 1208),
                  },
                  {
                    title: 'Survey',
                    path: paths.dashboard.define.competition.survey,
                    moduleId: getModuleId('Survey', 1209),
                    sortId: getModuleSortId('Survey', 1209),
                  },
                  {
                    title: 'Survey Question',
                    path: paths.dashboard.define.competition.survey_question,
                    moduleId: getModuleId('Survey Question', 1210),
                    sortId: getModuleSortId('Survey Question', 1210),
                  },
                ],
              },
              {
                title: 'Judging',
                path: paths.dashboard.define.judgement.root,
                moduleId: getModuleId('Judging', 1250),
                sortId: getModuleSortId('Judging', 1250),
                children: [
                  {
                    title: 'Judging Period',
                    path: paths.dashboard.define.judgement.judgement_period,
                    moduleId: getModuleId('Judging Period', 1251),
                    sortId: getModuleSortId('Judging Period', 1251),
                  },
                  {
                    title: 'Judging Criterion',
                    path: paths.dashboard.define.judgement.judgement_criterion,
                    moduleId: getModuleId('Judging Criterion', 1251),
                    sortId: getModuleSortId('Judging Criterion', 1251),
                  },
                  {
                    title: 'Judging Criterion Category',
                    path: paths.dashboard.define.judgement.judgement_criterion_category,
                    moduleId: getModuleId('Judging Criterion Category', 1252),
                    sortId: getModuleSortId('Judging Criterion Category', 1252),
                  },
                ],
              },
              {
                title: 'Authorization',
                path: paths.dashboard.define.authorization.root,
                moduleId: getModuleId('Authorization', 1300),
                sortId: getModuleSortId('Authorization', 1300),
                children: [
                  {
                    title: 'Role',
                    path: paths.dashboard.define.authorization.role,
                    moduleId: getModuleId('Role', 1301),
                    sortId: getModuleSortId('Role', 1301),
                  },
                  {
                    title: 'Module',
                    path: paths.dashboard.define.permission.module,
                    moduleId: getModuleId('Module', 1302),
                    sortId: getModuleSortId('Module', 1302),
                  },
                  {
                    title: 'Permission',
                    path: paths.dashboard.define.permission.permission,
                    moduleId: getModuleId('Permission', 1303),
                    sortId: getModuleSortId('Permission', 1303),
                  },
                  {
                    title: 'Module Permission',
                    path: paths.dashboard.define.permission.module_permission,
                    moduleId: getModuleId('Module Permission', 1304),
                    sortId: getModuleSortId('Module Permission', 1304),
                  },
                  {
                    title: 'Role Module Permission',
                    path: paths.dashboard.define.authorization.role_module_permission,
                    moduleId: getModuleId('Role Module Permission', 1305),
                    sortId: getModuleSortId('Role Module Permission', 1305),
                  },
                  {
                    title: 'Page Guideline',
                    path: paths.dashboard.define.permission.page_guideline,
                    moduleId: getModuleId('Page Guideline', 1306),
                    sortId: getModuleSortId('Page Guideline', 1306),
                  },
                ],
              },
              {
                title: 'Discipline',
                path: paths.dashboard.define.discipline.root,
                moduleId: getModuleId('Discipline', 1400),
                sortId: getModuleSortId('Discipline', 1400),
                children: [
                  {
                    title: 'Category',
                    path: paths.dashboard.define.discipline.category,
                    moduleId: getModuleId('Category', 1401),
                    sortId: getModuleSortId('Category', 1401),
                  },
                  {
                    title: 'Subject',
                    path: paths.dashboard.define.discipline.subject,
                    moduleId: getModuleId('Subject', 1402),
                    sortId: getModuleSortId('Subject', 1402),
                  },
                ],
              },
              {
                title: 'Accommodation',
                path: paths.dashboard.define.accommodation.root,
                moduleId: getModuleId('Accommodation', 1500),
                sortId: getModuleSortId('Accommodation', 1500),
                children: [
                  {
                    title: 'Room Occupant Role',
                    path: paths.dashboard.define.accommodation.room_occupant_role,
                    moduleId: getModuleId('Room Occupant Role', 1501),
                    sortId: getModuleSortId('Room Occupant Role', 1501),
                  },
                  {
                    title: 'Room Type',
                    path: paths.dashboard.define.accommodation.room_type,
                    moduleId: getModuleId('Room Type', 1502),
                    sortId: getModuleSortId('Room Type', 1502),
                  },
                ],
              },
              {
                title: 'Travel',
                path: paths.dashboard.define.travel.root,
                moduleId: getModuleId('Travel', 1600),
                sortId: getModuleSortId('Travel', 1600),
                children: [
                  {
                    title: 'Terminal',
                    path: paths.dashboard.define.travel.terminal,
                    moduleId: getModuleId('Terminal', 1601),
                    sortId: getModuleSortId('Terminal', 1601),
                  },
                  {
                    title: 'Transport Location',
                    path: paths.dashboard.define.travel.transport_location,
                    moduleId: getModuleId('Transport Location', 1602),
                    sortId: getModuleSortId('Transport Location', 1602),
                  },
                  {
                    title: 'Transport Company',
                    path: paths.dashboard.define.travel.transport_company,
                    moduleId: getModuleId('Transport Company', 1603),
                    sortId: getModuleSortId('Transport Company', 1603),
                  },
                ],
              },
              {
                title: 'Schedule',
                path: paths.dashboard.define.schedule.root,
                moduleId: getModuleId('Schedule', 1700),
                sortId: getModuleSortId('Schedule', 1700),
                children: [
                  {
                    title: 'Schedule Event',
                    path: paths.dashboard.define.schedule.schedule_event,
                    moduleId: getModuleId('Schedule Event', 1701),
                    sortId: getModuleSortId('Schedule Event', 1701),
                  },
                  {
                    title: 'Schedule Event Category',
                    path: paths.dashboard.define.schedule.schedule_event_category,
                    moduleId: getModuleId('Schedule Event Category', 1702),
                    sortId: getModuleSortId('Schedule Event Category', 1702),
                  },
                ],
              },
            ],
          },
          {
            title: 'Super Define',
            path: paths.dashboard.super_define.root,
            icon: ICONS.superDefine,
            moduleId: getModuleId('Super Define', 18),
            sortId: getModuleSortId('Super Define'),
            children: [
              {
                title: 'Organization',
                path: paths.dashboard.super_define.organization.root,
                moduleId: getModuleId('Organization', 2100),
                sortId: getModuleId('Organization', 2100),
                children: [
                  {
                    title: 'Organization Type',
                    path: paths.dashboard.super_define.organization.organization_type,
                    moduleId: getModuleId('Organization Type', 2101),
                    sortId: getModuleSortId('Organization Type', 2101),
                  },
                  {
                    title: 'Organization Sub Type',
                    path: paths.dashboard.super_define.organization.organization_sub_type,
                    moduleId: getModuleId('Organization Sub Type', 2102),
                    sortId: getModuleSortId('Organization Sub Type', 2102),
                  },
                ],
              },
              {
                title: 'Define System',
                path: paths.dashboard.define.system.root,
                moduleId: getModuleId('Define System', 2200),
                sortId: getModuleSortId('Define System', 2200),
                children: [
                  {
                    title: 'Academic Year',
                    path: paths.dashboard.define.system.academic_year,
                    moduleId: getModuleId('Academic Year', 2201),
                    sortId: getModuleSortId('Academic Year', 2201),
                  },
                  {
                    title: 'Address Type',
                    path: paths.dashboard.define.system.address_type,
                    moduleId: getModuleId('Address Type', 222),
                    sortId: getModuleSortId('Address Type', 2202),
                  },
                  {
                    title: 'Certificate Type',
                    path: paths.dashboard.define.system.certificate_type,
                    moduleId: getModuleId('Certificate Type', 2203),
                    sortId: getModuleSortId('Certificate Type', 2203),
                  },
                  {
                    title: 'Competition Type',
                    path: paths.dashboard.define.system.competition_type,
                    moduleId: getModuleId('Competition Type', 2204),
                    sortId: getModuleSortId('Competition Type', 2204),
                  },
                  {
                    title: 'Document Format Type',
                    path: paths.dashboard.define.system.document_format_type,
                    moduleId: getModuleId('Document Format Type', 2205),
                    sortId: getModuleSortId('Document Format Type', 2205),
                  },
                  {
                    title: 'Document Type',
                    path: paths.dashboard.define.system.document_type,
                    moduleId: getModuleId('Document Type', 2206),
                    sortId: getModuleSortId('Document Type', 2206),
                  },
                  {
                    title: 'Email Type',
                    path: paths.dashboard.define.system.email_type,
                    moduleId: getModuleId('Email Type', 2207),
                    sortId: getModuleSortId('Email Type', 2207),
                  },
                  {
                    title: 'Language',
                    path: paths.dashboard.define.system.language,
                    moduleId: getModuleId('Language', 2208),
                    sortId: getModuleSortId('Language', 2208),
                  },
                  {
                    title: 'Phone Type',
                    path: paths.dashboard.define.system.phone_type,
                    moduleId: getModuleId('Phone Type', 2209),
                    sortId: getModuleSortId('Phone Type', 2209),
                  },
                  {
                    title: 'Semester',
                    path: paths.dashboard.define.system.semester,
                    moduleId: getModuleId('Semester', 2210),
                    sortId: getModuleSortId('Semester', 2210),
                  },
                ],
              },
              {
                title: 'Authorization',
                path: paths.dashboard.define.authorization.root,
                moduleId: getModuleId('Authorization', 2300),
                sortId: getModuleSortId('Authorization', 2300),
                children: [
                  {
                    title: 'Group Role',
                    path: paths.dashboard.define.authorization.group_role,
                    moduleId: getModuleId('Group Role', 2301),
                    sortId: getModuleSortId('Group Role', 2301),
                  },
                  {
                    title: 'Group Role Organization',
                    path: paths.dashboard.define.authorization.group_role_organization,
                    moduleId: getModuleId('Group Role Organization', 2302, 2302),
                    sortId: getModuleSortId('Group Role Organization'),
                  },
                  {
                    title: 'Group Role Organization Module',
                    path: paths.dashboard.define.authorization.group_role_organization_module,
                    moduleId: getModuleId('Group Role Organization Module', 2303),
                    sortId: getModuleSortId('Group Role Organization Module', 2303),
                  },
                ],
              },
              {
                title: 'Discipline',
                path: paths.dashboard.define.discipline.root,
                moduleId: getModuleId('Discipline', 2400),
                sortId: getModuleSortId('Discipline', 2400),
                children: [
                  {
                    title: 'Sub Discipline',
                    path: paths.dashboard.define.discipline.sub_discipline,
                    moduleId: getModuleId('Sub Discipline', 2401),
                    sortId: getModuleSortId('Sub Discipline', 2401),
                  },
                  {
                    title: 'Topic',
                    path: paths.dashboard.define.discipline.topic,
                    moduleId: getModuleId('Topic', 2402),
                    sortId: getModuleSortId('Topic', 2402),
                  },
                  {
                    title: 'Sub Topic',
                    path: paths.dashboard.define.discipline.sub_topic,
                    moduleId: getModuleId('Sub Topic', 2403),
                    sortId: getModuleSortId('Sub Topic', 2403),
                  },
                ],
              },
              {
                title: 'Geography',
                path: paths.dashboard.define.geography.root,
                moduleId: getModuleId('Geography', 2500),
                sortId: getModuleSortId('Geography', 2500),
                children: [
                  {
                    title: 'Continent',
                    path: paths.dashboard.define.geography.continent,
                    moduleId: getModuleId('Continent', 2501),
                    sortId: getModuleSortId('Continent', 2501),
                  },
                  {
                    title: 'Country',
                    path: paths.dashboard.define.geography.country,
                    moduleId: getModuleId('Country', 2502),
                    sortId: getModuleSortId('Country', 2502),
                  },
                  {
                    title: 'Region',
                    path: paths.dashboard.define.geography.region,
                    moduleId: getModuleId('Region', 2503),
                    sortId: getModuleSortId('Region', 2503),
                  },
                  {
                    title: 'Province',
                    path: paths.dashboard.define.geography.province,
                    moduleId: getModuleId('Province', 2504),
                    sortId: getModuleSortId('Province', 2504),
                  },
                  {
                    title: 'District',
                    path: paths.dashboard.define.geography.district,
                    moduleId: getModuleId('District', 2505),
                    sortId: getModuleSortId('District', 2505),
                  },
                ],
              },
              {
                title: 'Company Insight',
                path: paths.dashboard.define.company_insight.root,
                moduleId: getModuleId('Company Insight', 2600),
                sortId: getModuleSortId('Company Insight', 2600),
                children: [
                  {
                    title: 'Unit',
                    path: paths.dashboard.define.company_insight.unit,
                    moduleId: getModuleId('Unit', 2601),
                    sortId: getModuleSortId('Unit', 2601),
                  },
                  {
                    title: 'Department',
                    path: paths.dashboard.define.company_insight.department,
                    moduleId: getModuleId('Department', 2602),
                    sortId: getModuleSortId('Department', 2602),
                  },
                  {
                    title: 'Office',
                    path: paths.dashboard.define.company_insight.office,
                    moduleId: getModuleId('Office', 2603),
                    sortId: getModuleSortId('Office', 2603),
                  },
                  {
                    title: 'Group',
                    path: paths.dashboard.define.company_insight.group,
                    moduleId: getModuleId('Group', 2604),
                    sortId: getModuleSortId('Group', 2604),
                  },
                ],
              },
              {
                title: 'Recheck',
                path: paths.dashboard.define.recheck.root,
                moduleId: getModuleId('Recheck', 2700),
                sortId: getModuleSortId('Recheck', 2700),
                children: [
                  {
                    title: 'User Organization',
                    path: paths.dashboard.define.recheck.user_organization,
                    moduleId: getModuleId('User Organization', 2701),
                    sortId: getModuleSortId('User Organization', 2701),
                  },
                  {
                    title: 'Group Role Join',
                    path: paths.dashboard.define.recheck.group_role_join,
                    moduleId: getModuleId('Group Role Join', 2702),
                    sortId: getModuleSortId('Group Role Join', 2702),
                  },
                  {
                    title: 'Unknown User',
                    path: paths.dashboard.define.recheck.unknown_user,
                    moduleId: getModuleId('Unknown User', 2703),
                    sortId: getModuleSortId('Unknown User', 2703),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        subheader: 'Extras',
        items: [],
      },
    ],
    [getModuleId, getModuleSortId]
  );

  // Sıralanmış ve filtrelenmiş navData'yı memoize et
  const processedNavData = useMemo(() => {
    // Apply sorting to navData
    const sortedNavData = navData.map((section) => ({
      ...section,
      items: sortItemsByOrder(section.items),
    }));

    // Recursive function to filter items based on visibility permissions
    const filterItemsByPermission = (items) => {
      if (!items) return [];

      return items
        .map((item) => {
          // First check if this item is visible
          const isVisible = !visibleModules || visibleModules.includes(item.moduleId);

          if (!isVisible) return null;

          // If this item has children, filter them recursively
          if (item.children && item.children.length) {
            // Özel durum: Define (17) ve Super Define (18) modülleri için tüm alt modülleri göster
            if (item.moduleId === 17 || item.moduleId === 18) {
              return item; // Alt modülleri filtrelemeden direkt olarak göster
            }

            const filteredChildren = filterItemsByPermission(item.children);

            // Only show parent if it has visible children or if parent itself should be shown
            if (filteredChildren.length > 0) {
              return {
                ...item,
                children: filteredChildren,
              };
            }

            // If no children are visible, don't show the parent either
            return null;
          }

          // Return the item without changes if it has no children
          return item;
        })
        .filter(Boolean); // Remove null items
    };

    // Filter navData based on visibleModules
    // extras alanı herkese görünecek
    return sortedNavData
      .map((section) => ({
        ...section,
        items:
          section.subheader === 'Extras' && RoleHelpers.isSuperAdmin(user)
            ? section.items
            : filterItemsByPermission(section.items),
      }))
      .filter((section) => section.items.length > 0);
  }, [navData, sortItemsByOrder, visibleModules, user]);
  // Modül yükleme hatası varsa, hata mesajı göster
  if (modulesError && !modulesLoading) {
    console.error('Error loading modules:', modulesError);
    // Hata durumunda bile UI'ın çalışmaya devam etmesi için varsayılan değerleri kullan
  }

  return processedNavData;
};
